import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import { useProcManageActions } from '@/features/proc-management';
import {
  LogicRecordResponse,
  UpdateLogicRequest,
  useGetLogicsByProcId,
} from '@/shared/api';
import { themeConfig, useThemeContext } from '@/shared/theme';
import {
  ConditionComponentsType,
  DashboardSidebarRightTabsType,
  LogicRule,
} from '@/shared/types';
import { Col, Row } from '@/shared/ui';
import { LogicTabContext } from '@/widgets/proc-logic/ProcLogicTabProvider';
import { ProcLogicRule } from './ProcLogicRule';

const { Text } = Typography;

export const ProcLogicTab = () => {
  const { theme } = useThemeContext();
  const {
    selectedStep,
    proc,
    setRightSidebarActiveTab,
    changesSaved,
    setChangesSaved,
    resetLogicCount,
    setResetLogicCount,
  } = useProcContext();

  const endRef = useRef<HTMLDivElement>(null);

  const [rules, setRules] = useState<LogicRule[]>([]);
  const [defaultGoToStep, setDefaultGoToStep] = useState<string>();

  const {
    data: logicsData,
    isLoading: logicsDataLoading,
    refetch: refetchLogic,
  } = useGetLogicsByProcId({ pathParams: { procId: proc?.id || '' } });
  const { saveStepLogic } = useProcManageActions();

  const conditioningAvailable = useMemo(
    () =>
      Boolean(
        selectedStep?.components?.find((item) =>
          Object.values(ConditionComponentsType).includes(
            item?.component as ConditionComponentsType,
          ),
        ),
      ),
    [selectedStep?.components],
  );

  const stepLogicData = useMemo(
    () =>
      logicsData?.stepLogics.filter(
        (logic) => logic.stepId === selectedStep?.id,
      )[0],
    [logicsData, selectedStep?.id],
  );

  const addRuleBtnDisabled = useMemo(
    () =>
      Boolean(
        rules.find(
          (rule) =>
            !rule.component ||
            !rule.operator ||
            (!rule.value && rule.value !== false) ||
            !rule.goToStep,
        ),
      ),
    [rules],
  );

  const saveBtnDisabled = useMemo(
    () =>
      Boolean(
        changesSaved ||
          rules.find(
            (rule) =>
              !rule.component ||
              !rule.operator ||
              (!rule.value && rule.value !== false) ||
              !rule.goToStep,
          ),
      ),
    [rules, changesSaved],
  );

  const stepsTitlesOptions = useMemo(() => {
    const items = [];
    proc?.stepData?.steps.forEach((stepItem) => {
      if (stepItem.id !== selectedStep?.id) {
        items.push({
          label: stepItem.title,
          value: stepItem.id,
        });
      }
    });
    items.push({
      label: <Divider style={{ margin: 0, marginBottom: -10, minHeight: 2 }} />,
      options: [
        {
          label: 'Next step',
          value: 'nextStep',
          style: {
            padding: '5px 12px',
          },
        },
        {
          label: 'Proc end',
          value: logicsData?.endStep || 'procEnd',
          style: {
            padding: '5px 12px',
          },
        },
      ],
    });

    return items;
  }, [logicsData?.endStep, proc?.stepData?.steps, selectedStep?.id]);

  const handleDefaultStepChange = (value: string) => {
    setChangesSaved(false);
    setDefaultGoToStep(value);
  };

  const addRule = useCallback(() => {
    setRules((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        component: undefined,
        operator: 'is',
        value: '',
        goToStep: '',
      },
    ]);
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 10);
  }, []);

  const onCancel = () => {
    setChangesSaved(true);
    setResetLogicCount(resetLogicCount! + 1);
    setRightSidebarActiveTab(DashboardSidebarRightTabsType.Components);
  };

  const handleRulesSave = () => {
    saveStepLogic({
      stepId: selectedStep?.id || '',
      data: {
        logics: rules as UpdateLogicRequest[],
        defaultGoToStep:
          defaultGoToStep === 'nextStep' ? null : defaultGoToStep,
      },
      onSuccess: () => {
        refetchLogic();
        setChangesSaved(true);
        message.success('Logic saved successfully');
      },
    });
  };

  useEffect(() => {
    if (!stepLogicData) return;

    const savedDefaultStep = proc?.stepData?.steps.find(
      (stepItem) => stepItem.id === stepLogicData?.defaultGoToStep,
    );

    if (!savedDefaultStep) {
      const currentStepIndex = proc?.stepData?.steps.findIndex(
        (stepItem) => stepItem.id === selectedStep?.id,
      );
      if (
        !proc?.stepData?.steps[Number(currentStepIndex) + 1] ||
        (logicsData?.endStep &&
          stepLogicData?.defaultGoToStep === logicsData?.endStep) ||
        stepLogicData?.defaultGoToStep === 'procEnd'
      ) {
        setDefaultGoToStep(stepLogicData?.endStep || 'procEnd');
        return;
      }
      setDefaultGoToStep('nextStep');
      return;
    }
    if (savedDefaultStep.id === logicsData?.endStep) {
      setDefaultGoToStep(logicsData?.endStep);
      return;
    }
    setDefaultGoToStep(stepLogicData?.defaultGoToStep);
  }, [stepLogicData, resetLogicCount, proc?.stepData?.steps.length]);

  useEffect(() => {
    setRules((stepLogicData?.logics || []) as LogicRecordResponse[]);
  }, [stepLogicData?.logics, resetLogicCount]);

  if (logicsDataLoading) {
    return (
      <div style={{ paddingLeft: 30 }}>
        <Spin />
      </div>
    );
  }

  return (
    <LogicTabContext.Provider value={{ rules, setRules }}>
      <Wrapper alignItems="space-between">
        <div
          style={{ fontSize: 15, alignSelf: 'flex-start', padding: '5px 15px' }}
        >
          Edit logic for{' '}
          <span style={{ fontWeight: 'bold' }}>{selectedStep?.title}</span>
        </div>
        <ContentWrapper>
          {conditioningAvailable ? (
            <>
              <Space
                direction="vertical"
                size={20}
                style={{ width: '100%', padding: '5px 15px' }}
              >
                <ScrollingContentWrapper
                  direction="vertical"
                  size={15}
                  style={{ width: '100%' }}
                >
                  {rules.map((rule, index) => (
                    <Fragment key={rule.id}>
                      <ProcLogicRule
                        key={rule.id}
                        rule={rule}
                        stepLogicsData={stepLogicData?.logics}
                        endStepId={stepLogicData?.endStep}
                      />
                      {rules.length - 1 !== index && <div ref={endRef}></div>}
                    </Fragment>
                  ))}
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={addRuleBtnDisabled}
                    onClick={addRule}
                  >
                    Add rule
                  </Button>
                </ScrollingContentWrapper>
              </Space>
            </>
          ) : (
            <div style={{ padding: '5px 15px' }}>
              <Text style={{ opacity: 0.6 }}>
                In order to use logic, please add one of these components to
                selected step:{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {Object.values(ConditionComponentsType).join(', ')}
                </span>
              </Text>
            </div>
          )}
          <div style={{ paddingInline: 15 }}>
            <DefaultStepWrapper
              gap={15}
              justifyContent="space-between"
              style={{
                background: themeConfig[theme].colors.bgBase,
              }}
            >
              <div>Default</div>
              <Row style={{ width: 'fit-content' }}>
                <div style={{ whiteSpace: 'nowrap' }}>go to</div>
              </Row>
              <Select
                popupClassName="logic-tab-default-step-select"
                options={stepsTitlesOptions}
                value={defaultGoToStep}
                onSelect={handleDefaultStepChange}
                style={{ width: '100%' }}
              />
            </DefaultStepWrapper>
          </div>
        </ContentWrapper>
        <ButtonsWrapper
          gap={15}
          style={{
            background: themeConfig[theme].colors.contentBg,
            borderColor: themeConfig[theme].colors.border,
          }}
        >
          <Button style={{ marginLeft: 'auto' }} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={saveBtnDisabled}
            onClick={handleRulesSave}
          >
            Save and continue
          </Button>
        </ButtonsWrapper>
      </Wrapper>
    </LogicTabContext.Provider>
  );
};

const Wrapper = styled(Col)`
  height: 100%;
`;

const ContentWrapper = styled.div`
  margin-top: 30px;
  width: 100%;
  overflow-y: auto;
  flex: 1 1 0px;
`;

const DefaultStepWrapper = styled(Row)`
  position: relative;
  margin-top: 30px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
  margin-bottom: 20px;
  overflow-y: auto;
`;

const ButtonsWrapper = styled(Row)`
  width: 100%;
  padding: 8px 10px;
  height: 70px;
  flex: 0 0 auto;
  box-shadow: rgba(0, 0, 0, 0.26) 2px 1px 5px;
  border-top: 1px solid;
`;

const ScrollingContentWrapper = styled(Space)`
  overflow-y: auto;
`;
